/** XeAgents.ai**/
/**
 * The two rules the page turns on, as plain functions.
 *
 * Pulled out of the hook so they can be read and tested without React: one
 * decides what a regeneration is allowed to replace, the other decides what
 * stops Save. Both are easy to re-implement slightly differently in each section
 * that needs them, and both are wrong in ways nobody notices until a call goes
 * out - which is exactly the pair worth stating once.
 */

import type { AIContentState, ContentOrigin, SalesContentField, TemplateVariable } from "./types";

export const SALES_FIELDS: SalesContentField[] = ['valueProposition', 'keyFeatures', 'objections'];

/**
 * Write generated copy into the fields, without taking back anything the user
 * has touched.
 *
 * `force` is the one field whose explicit Regenerate was just confirmed. Every
 * other edited field is left exactly as typed, which is what makes a re-extract
 * safe to run at any point rather than something to warn about.
 */
export function applyGeneratedContent(
  current: Record<SalesContentField, AIContentState>,
  incoming: Partial<Record<SalesContentField, string>>,
  force: SalesContentField[] = [],
): Record<SalesContentField, AIContentState> {
  const next = { ...current };
  for (const field of SALES_FIELDS) {
    const text = (incoming[field] || '').trim();
    if (!text) continue;
    if (current[field].origin === 'edited' && !force.includes(field)) continue;
    next[field] = { value: text, origin: 'ai' };
  }
  return next;
}

/**
 * Write generated values into the template's own placeholders.
 *
 * Same promise as applyGeneratedContent, for the fields under "Values this
 * template speaks": anything the user typed is theirs and survives a
 * regeneration, unless this was the field whose own rewrite they just
 * confirmed. Separate from the sales version because these are a keyed bag
 * rather than three known fields, and collapsing the two would mean a generated
 * value could land in a variable the template never declared.
 */
export function applyGeneratedValues(
  current: Record<string, string>,
  origins: Record<string, ContentOrigin>,
  incoming: Record<string, string>,
  force: string[] = [],
): { values: Record<string, string>; origins: Record<string, ContentOrigin> } {
  const values = { ...current };
  const nextOrigins = { ...origins };

  for (const [key, raw] of Object.entries(incoming || {})) {
    const text = String(raw ?? '').trim();
    if (!text) continue;
    if (origins[key] === 'edited' && !force.includes(key)) continue;
    values[key] = text;
    nextOrigins[key] = 'ai';
  }

  return { values, origins: nextOrigins };
}

/**
 * Values the audience on the page already answers.
 *
 * {{target_audience}} is written from the Industry the person picked, and {{value_proposition}} falls back to the
 * value proposition that was written FOR that buyer. A website read after the industry was chosen used to
 * overwrite both with a generic line about the company ("enterprise sales and ops teams"), which is how a
 * clinic got a pitch for sales teams. So once an industry is chosen the scan must not write them.
 */
export const AUDIENCE_OWNED_KEYS = ['target_audience', 'value_proposition'];

export function withoutAudienceOwnedValues(found: Record<string, string>, hasIndustry: boolean): Record<string, string> {
  if (!hasIndustry) return found;
  return Object.fromEntries(Object.entries(found).filter(([key]) => !AUDIENCE_OWNED_KEYS.includes(key)));
}

export interface AudienceRefreshPlan {
  /** Sales fields the AI wrote and the person has not touched. */
  fields: SalesContentField[];
  /** The template's own placeholders that were filled in by the scan or the AI and not typed by the person. */
  variableKeys: string[];
  /** The call instructions, when the AI drafted them and they have not been edited. */
  instructions: boolean;
  /** A generic value proposition from the scan, to be dropped so the one written for this buyer is used. */
  clearValueProposition: boolean;
}

/**
 * What to rewrite when the audience changes: everything the AI wrote, nothing the person did.
 *
 * Anything with origin 'edited' (typed by the person, or a flow reopened from a saved one) is left exactly as it is.
 */
export function audienceRefreshPlan(input: {
  salesContent: Record<SalesContentField, AIContentState>;
  variableKeys: string[];
  variables: Record<string, string>;
  variableOrigins: Record<string, ContentOrigin>;
  instructions: AIContentState;
}): AudienceRefreshPlan {
  const fields = SALES_FIELDS.filter((f) => input.salesContent[f].value.trim() && input.salesContent[f].origin === 'ai');
  const variableKeys = input.variableKeys.filter((key) => {
    const origin = input.variableOrigins[key];
    return origin !== 'edited' && origin !== 'generating';
  });
  return {
    fields,
    variableKeys,
    instructions: input.instructions.origin === 'ai' && input.instructions.value.trim().length > 0,
    clearValueProposition: !!input.variables.value_proposition?.trim() && input.variableOrigins.value_proposition !== 'edited',
  };
}

export function planHasWork(plan: AudienceRefreshPlan): boolean {
  return plan.fields.length > 0 || plan.variableKeys.length > 0 || plan.instructions || plan.clearValueProposition;
}

/**
 * Settle placeholders left spinning by a run that did not write them.
 *
 * Mirrors settleGenerating: a value the model skipped goes back to what it was
 * rather than showing a skeleton for ever.
 */
export function settleGeneratingValues(
  current: Record<string, string>,
  origins: Record<string, ContentOrigin>,
  keys: string[],
): Record<string, ContentOrigin> {
  const next = { ...origins };
  for (const key of keys) {
    if (next[key] !== 'generating') continue;
    next[key] = String(current[key] ?? '').trim() ? 'edited' : 'empty';
  }
  return next;
}

/**
 * Settle a field that was left spinning.
 *
 * A model that returned nothing for one of three fields must not leave that
 * field showing a skeleton for ever. It goes back to what it was: the user's
 * text if there was any, otherwise empty.
 */
export function settleGenerating(
  current: Record<SalesContentField, AIContentState>,
  fields: SalesContentField[],
): Record<SalesContentField, AIContentState> {
  const next = { ...current };
  for (const field of fields) {
    if (next[field].origin !== 'generating') continue;
    next[field] = { value: next[field].value, origin: next[field].value.trim() ? 'edited' : 'empty' };
  }
  return next;
}

export interface ValidationInput {
  flowName: string;
  language: string;
  /** Trimmed value of a template variable. */
  variableValue: (key: string) => string;
  salesContent: Record<SalesContentField, AIContentState>;
  /** The template's own fields, minus the four the form asks for by name. */
  extraVariables: TemplateVariable[];
  /** Resolved audience profile: only the answers that were actually given. */
  icp?: Record<string, string>;
}

/**
 * The Step 2 answers a website-led run has to have.
 *
 * One button now reads the site and writes the whole of Step 3 from it, so these
 * stop being nice-to-haves: they are the difference between copy written for a
 * named buyer and copy written for nobody. "Anything else worth knowing" stays
 * optional - it is a catch-all, and a required field that often has nothing true
 * to put in it only teaches people to type filler.
 */
export const WEBSITE_REQUIRED_ICP_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'industry', label: 'Industry' },
  { key: 'companySize', label: 'Company size' },
  { key: 'region', label: 'Region' },
];

/**
 * Everything in Step 2, with the name each field goes by on screen.
 *
 * Named rather than counted because "fill in the 3 fields marked above" is only
 * useful when something is actually marked, and a dropdown nobody has touched
 * carries no error yet - which left a disabled button next to a hint pointing
 * at nothing. Naming them needs no marking.
 *
 * `industryOther` has no field of its own until "Other" is picked, so it takes
 * Industry's name: a message about a box that is not on screen helps nobody.
 */
export const STEP2_FIELD_LABELS: Record<string, string> = {
  company_name: 'Company Name',
  product_name: 'Product / Service',
  industry: 'Industry',
  industryOther: 'Industry',
  companySize: 'Company size',
  region: 'Region',
};

export const STEP2_REQUIRED_KEYS: string[] = Object.keys(STEP2_FIELD_LABELS);

/**
 * Which Step 2 answers are still missing, given what validation already found.
 *
 * Step 3 is written FROM these, and the button that writes it replaces all three
 * of its fields at once - so running early does not just produce a script for
 * nobody, it overwrites whatever was there to do it. Hence a gate rather than a
 * warning. Reading the gaps out of `collectErrors` rather than re-deriving them
 * keeps the button and Save agreeing about what "filled in" means; when no
 * website is given, that function stops requiring the audience answers and these
 * gaps shrink to match, which is what keeps the type-it-all-in path open.
 */
export function step2Gaps(errors: Record<string, string>): string[] {
  return Object.keys(errors).filter(key => STEP2_REQUIRED_KEYS.includes(key));
}

/** The missing Step 2 fields as a reader would list them: "A, B and C". */
export function describeStep2Gaps(errors: Record<string, string>): string {
  const names = [...new Set(step2Gaps(errors).map(key => STEP2_FIELD_LABELS[key] ?? key))];
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Everything that must be filled before this template can be saved.
 *
 * The three sales fields are mandatory because they are the first thirty seconds
 * of the call. The template's own fields are mandatory only where they have no
 * fallback copy: one with a fallback degrades to a duller sentence, one without
 * is read out as "{{proof_point}}" to a customer, which is why the server
 * refuses that clone too rather than trusting this check.
 */
export function collectErrors({
  flowName,
  language,
  variableValue,
  salesContent,
  extraVariables,
  icp = {},
}: ValidationInput): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!flowName.trim()) errors.flowName = "Give your template a name.";
  if (!language) errors.language = "Choose the language the agent will speak.";
  if (!variableValue('company_name')) errors.company_name = "This field is required.";
  if (!variableValue('product_name')) errors.product_name = "This field is required.";

  // Required whether or not a website was given, which they were not before.
  // Industry is the one Target Audience is derived from, and Target Audience is
  // spoken in the greeting and the pitch - so a blank here is not a duller
  // script, it is a sentence with a hole in it. The other two are three seconds
  // of work once somebody is already choosing an industry.
  //
  // `icp` arrives already resolved by icpForRequest, so "Other" with nothing
  // typed beside it shows up here as a missing industry rather than needing a
  // rule of its own - which is the behaviour we want: picking "Other" and
  // naming nothing has told us no more than picking nothing did.
  for (const field of WEBSITE_REQUIRED_ICP_FIELDS) {
    if (!String(icp[field.key] ?? '').trim()) errors[field.key] = "This field is required.";
  }

  for (const field of SALES_FIELDS) {
    if (!salesContent[field].value.trim()) errors[field] = "This field is required.";
  }

  for (const variable of extraVariables) {
    if (variable.required && !variableValue(variable.key)) {
      errors[variable.key] = "This field is required.";
    }
  }

  return errors;
}
